import { isEqual, isNumber } from 'lodash-es';
import {
  computed,
  getCurrentInstance,
  nextTick,
  reactive,
  readonly,
  toRaw,
  unref,
  watchEffect,
} from 'vue';
import type { Recordable } from '@mts-fe/types';
import type { DeepReadonly, Ref, UnwrapRef, WritableComputedRef } from 'vue';

export function useRuleFormItem<T extends Recordable, K extends keyof T, V = UnwrapRef<T[K]>>(
  props: T,
  key?: K,
  changeEvent?,
  emitData?: Ref<any[]>,
): [WritableComputedRef<V>, (val: V) => void, DeepReadonly<V>];

export function useRuleFormItem<T extends Recordable>(
  props: T,
  key: keyof T = 'value',
  changeEvent = 'change',
  emitData?: Ref<any[]>,
) {
  const instance = getCurrentInstance();
  const emit = instance?.emit;

  const numberToString = (value: T[keyof T]) => (isNumber(value) ? value.toString() : value);

  const innerState = reactive({
    value: numberToString(props[key]),
  });

  const defaultState = readonly(innerState);

  const setState = (val: UnwrapRef<T[keyof T]>): void => {
    innerState.value = val as T[keyof T];
  };

  watchEffect(() => {
    innerState.value = numberToString(props[key]);
  });

  const state: any = computed({
    get() {
      return innerState.value;
    },
    set(value) {
      if (isEqual(value, defaultState.value)) return;

      innerState.value = value as T[keyof T];
      nextTick(() => {
        emit?.(changeEvent, value, ...(toRaw(unref(emitData)) || []));
      });
    },
  });

  return [state, setState, defaultState];
}
